import httpClient from "@/utils/httpClient";
import Vue from "vue";
import moment from 'moment'

export const smartLogisticWarehouse = {
    namespaced: true,

    state: {
        loadingQueue: [],
        updatingItem: false,
        completingPartialDelivery: false,
        curOrderId: null,
        internalOrders: null,
        internalOrderData: {},
        partialDeliveries: {},
        panels: [],
        currentSection: 'commissioning',
        selectedSortingType: 'date',
        selectedStatus: ['new', 'partially_delivered'],
        selectedDates: null,
        orderSent: false,

        config: {
            sections: ['commissioning', 'basket', 'order-history'],
            sortingTypes: [
                {text: 'Wohneinheit', value: 'livingUnit'},
                {text: 'Status', value: 'status'},
                {text: 'Datum', value: 'date'},
            ],
            statusOrderTypes: [
                {text: 'Neu', value: 'new'},
                {text: 'Teilgeliefert', value: 'partially_delivered'},
                {text: 'Abgeschlossen', value: 'done'}]
        }
    },

    getters: {
        curOrderId: state => state.curOrderId,

        internalOrders: state => {
            if (state.internalOrders === null) {
                return []
            }

            return state.internalOrders
        },
        activePanels: state => state.panels,

        isLoading: state => (id) => state.loadingQueue.includes('ib_' + id) || state.loadingQueue.includes('pd_' + id) || state.loadingQueue.includes(id),
        isUpdatingItem: state => state.updatingItem,
        isCompletingPartialOrder: state => state.completingPartialDelivery,

        orderById: state => (id) => state.internalOrderData[id],
        partialDeliveriesById: state => (id) => state.partialDeliveries[id],

        currentSection: state => state.currentSection,
        selectedSortingType: state => state.selectedSortingType,
        selectedStatus: state => state.selectedStatus,
        selectedDates: state => state.selectedDates,

        config: state => state.config,
    },

    actions: {
        /**
         *
         * @param commit
         * @param state
         * @param force
         */
        fetchInternalOrders({commit, state}, force) {
            if (!state.loadingQueue.includes('fetchInternalOrders') && (force || state.internalOrders === null)) {
                commit('setInternalOrders', [])
                state.loadingQueue.push('fetchInternalOrders')

                let endpoint = process.env.VUE_APP_API_LOGISTICS + 'internal-basket'

                if (state.selectedDates && state.selectedDates.length === 2) {
                    endpoint += `/${moment.utc(state.selectedDates[0]).unix()}-${moment.utc(state.selectedDates[1]).unix()}`
                }

                httpClient.get(endpoint)
                    .then(response => {
                        commit('setInternalOrders', response.data)
                    })
                    .finally(() => {
                        state.loadingQueue.splice(state.loadingQueue.indexOf('fetchInternalOrders'), 1)
                    })
            }
        },

        /**
         *
         * @param commit
         * @param state
         * @param id
         */
        fetchOrderData({commit, state, dispatch}, id) {
            if (!state.loadingQueue.includes('ib_' + id) && !state.loadingQueue.includes('pd_' + id)) {
                state.loadingQueue.push('ib_' + id)
                state.loadingQueue.push('pd_' + id)

                httpClient.get(process.env.VUE_APP_API_LOGISTICS + 'internal-basket/' + id)
                    .then(response => {
                        commit('setInternalOrderData', {id: id, data: response.data})
                    })
                    .finally(() => {
                        state.loadingQueue.splice(state.loadingQueue.indexOf('ib_' + id), 1)
                    })

                dispatch('fetchPartialDeliveries', id)
            }
        },

        fetchPartialDeliveries({commit, state}, id) {
            httpClient.get(process.env.VUE_APP_API_LOGISTICS + 'partial-delivery/by-internal-basket/' + id)
                .then(response => {
                    commit('setPartialDelivery', {id: id, data: response.data})
                })
                .finally(() => {
                    state.loadingQueue.splice(state.loadingQueue.indexOf('pd_' + id), 1)
                })
        },

        /**
         *
         * @param commit
         * @param state
         * @param item
         */
        updateItem({commit, state}, {partialDeliveryId, item}) {
            if (state.updatingItem) {
                return
            }

            state.updatingItem = true

            const payload = {
                artikelNrLAG: item.artikelNrLAG,
                ownerType: item.ownerType,
                ownerId: item.ownerId,
                quantity: item.quantity
            }

            httpClient.patch(process.env.VUE_APP_API_LOGISTICS + `partial-delivery/${partialDeliveryId}/item`, payload)
                .then(response => {
                    commit('updatePartialDeliveryItem', {partialDeliveryId, payload})
                })
                .finally(() => {
                    state.updatingItem = false
                })
        },

        /**
         *
         * @param commit
         * @param state
         * @param partialDeliveryId
         * @param item
         */
        deleteItem({commit, state}, {partialDeliveryId, item}) {
            if (state.updatingItem) {
                return
            }

            state.updatingItem = true

            const payload = {
                artikelNrLAG: item.artikelNrLAG,
                ownerType: item.ownerType,
                ownerId: item.ownerId,
                quantity: 0
            }

            httpClient.patch(process.env.VUE_APP_API_LOGISTICS + `partial-delivery/${partialDeliveryId}/item/delete`, payload)
                .then(response => {
                    commit('updatePartialDeliveryItem', {partialDeliveryId, payload})
                })
                .finally(() => {
                    state.updatingItem = false
                })
        },

        /**
         *
         * @param commit
         * @param state
         * @param partialDeliveryId
         * @param batch
         */
        updateItemBatch({commit, state}, {partialDeliveryId, batch}) {
            if (state.updatingItem) {
                return
            }

            state.updatingItem = true

            const payload = []

            batch.forEach(item => {
                payload.push({
                    artikelNrLAG: item.artikelNrLAG,
                    ownerType: item.ownerType,
                    ownerId: item.ownerId,
                    quantity: item.quantity
                })
            })

            httpClient.patch(process.env.VUE_APP_API_LOGISTICS + `partial-delivery/${partialDeliveryId}/item/batch`, payload)
                .then(response => {
                    commit('updatePartialDeliveryItemBatch', {partialDeliveryId, payload})
                })
                .finally(() => {
                    state.updatingItem = false
                })
        },

        /**
         *
         * @param commit
         * @param state
         * @param partialDeliveryId
         * @param batch
         */
        deleteItemBatch({commit, state}, {partialDeliveryId, batch}) {
            if (state.updatingItem) {
                return
            }

            state.updatingItem = true

            const payload = []

            batch.forEach(item => {
                payload.push({
                    artikelNrLAG: item.artikelNrLAG,
                    ownerType: item.ownerType,
                    ownerId: item.ownerId,
                    quantity: 0,
                    deleted: true,
                })
            })

            httpClient.patch(process.env.VUE_APP_API_LOGISTICS + `partial-delivery/${partialDeliveryId}/item/batch/delete`, payload)
                .then(response => {
                    commit('updatePartialDeliveryItemBatch', {partialDeliveryId, payload})
                })
                .finally(() => {
                    state.updatingItem = false
                })
        },

        /**
         *
         * @param state
         * @param partialDeliveryId
         */
        completePartialDelivery({commit, state}, partialDeliveryId) {
            if (state.completingPartialDelivery) {
                return
            }

            state.completingPartialDelivery = true

            httpClient.patch(process.env.VUE_APP_API_LOGISTICS + `partial-delivery/${partialDeliveryId}/complete`, {})
                .then(response => {
                    commit('completePartialDelivery', partialDeliveryId)
                    commit('addPartialDelivery', response.partialDelivery)
                    commit('updateInternalOrderStatus', response.status)
                })
                .finally(() => {
                    state.completingPartialDelivery = false
                })
        },

        generateOrder({commit, state}, regenerate) {

            if (state.loadingQueue.includes('generate_order')) {
                return
            }

            state.loadingQueue.push('generate_order')

            const uri = regenerate ? `warehouse/regenerate-order` : `warehouse/generate-order`

            httpClient.get(process.env.VUE_APP_API_LOGISTICS + uri)
                .then(response => {
                    commit('smartLogisticBasket/setBasket', response.data, {root: true})
                })
                .finally(() => {
                    state.loadingQueue.splice(state.loadingQueue.indexOf('generate_order'), 1)
                })
        },

        emptyOrder({commit, state}) {
            if (state.loadingQueue.includes('generate_order')) {
                return
            }

            state.loadingQueue.push('generate_order')

            httpClient.get(process.env.VUE_APP_API_LOGISTICS + `warehouse/empty-order`)
                .then(response => {
                    commit('smartLogisticBasket/setBasket', response.data, {root: true})
                })
                .finally(() => {
                    state.loadingQueue.splice(state.loadingQueue.indexOf('generate_order'), 1)
                })
        },

        submitOrder({state, commit, dispatch, rootGetters}) {
            state.loadingQueue.push('submitOrder')

            const basket = rootGetters['smartLogisticBasket/basket']

            httpClient.put(process.env.VUE_APP_API_LOGISTICS + `warehouse/basket/${basket.id}/confirm`, {
                customer_order_no: basket.customer_order_no,
                custom_order_date: basket.custom_order_date,
                comment: basket.comment
            })
                .then(() => {
                    basket.status = 'confirmed'
                    commit('smartLogisticBasket/setBasket', basket, {root: true})
                    //dispatch('generateOrder')
                })
                .finally(() => {
                    state.loadingQueue.splice(state.loadingQueue.indexOf('submitOrder'), 1)
                })
        }
    },

    mutations: {
        setCurrentOrderId(state, id) {
            state.curOrderId = id;
        },

        setPanels(state, panels) {
            state.panels = panels
        },

        setInternalOrders(state, val) {
            state.internalOrders = val;
        },

        setInternalOrderData(state, {id, data}) {
            Vue.set(state.internalOrderData, id, data)
        },

        updateInternalOrderStatus(state, status) {
            Vue.set(state.internalOrderData[state.curOrderId], 'status', status)

            const pos = state.internalOrders.findIndex(o => o.id === state.curOrderId)

            Vue.set(state.internalOrders[pos], 'status', status)
        },

        setPartialDelivery(state, {id, data}) {
            Vue.set(state.partialDeliveries, id, data)
        },

        completePartialDelivery(state, id) {
            const pos = state.partialDeliveries[state.curOrderId].findIndex(p => p.id === id)

            if (pos !== -1) {
                Vue.set(state.partialDeliveries[state.curOrderId][pos], 'status', 'submitted')
            }
        },

        addPartialDelivery(state, data) {
            state.partialDeliveries[state.curOrderId].push(data)
        },

        updatePartialDeliveryItem(state, {partialDeliveryId, payload}) {
            const partialDeliveries = state.partialDeliveries[state.curOrderId]
            const partialDelivery = partialDeliveries.find(p => p.id === partialDeliveryId)

            if (partialDelivery) {
                const pos = partialDelivery.items.findIndex(i => i.artikelNrLAG == payload.artikelNrLAG && i.ownerType == payload.ownerType && i.ownerId == payload.ownerId)

                if (pos !== -1) {
                    const newItem = Object.assign({}, partialDelivery.items[pos])
                    newItem.quantity = payload.quantity

                    // we need to replace whole object to keep the reactivity
                    Vue.set(partialDelivery.items, pos, newItem)
                }
            }
        },

        updatePartialDeliveryItemBatch(state, {partialDeliveryId, payload}) {
            const partialDeliveries = state.partialDeliveries[state.curOrderId]
            const partialDelivery = partialDeliveries.find(p => p.id === partialDeliveryId)

            if (partialDelivery) {
                payload.forEach(item => {
                    const pos = partialDelivery.items.findIndex(i => i.artikelNrLAG == item.artikelNrLAG && i.ownerType == item.ownerType && i.ownerId == item.ownerId)

                    if (pos !== -1) {
                        const newItem = Object.assign({}, partialDelivery.items[pos])
                        newItem.quantity = item.quantity
                        newItem.deleted = item.deleted

                        // we need to replace whole object to keep the reactivity
                        Vue.set(partialDelivery.items, pos, newItem)
                    }
                })
            }
        },


        setCurrentSection(state, section) {
            state.currentSection = section
        },

        setSelectedSortingType(state, type) {
            state.selectedSortingType = type
        },

        setSelectedStatus(state, status) {
            state.selectedStatus = status
        },

        setSelectedDates(state, date) {
            state.selectedDates = date
        },
    }
}
